import React from "react";
import { Badge, Box, Button, Flex, Text, useColorModeValue } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { DEFAULT_INCIDENT_META, INCIDENT_META } from "./notificationsConstants";

/**
 * Renders the currency lines carried by a treasury incident payload.
 * @param {Object} props - the notification payload
 * @returns {React.ReactElement|null}
 */
function TreasuryDetail({ payload }) {
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const rows = payload?.currencies;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return (
        <Box mt="8px">
            {rows.map((row) => (
                <Text key={row.currency} fontSize="xs" color={hintColor}>
                    {row.currency} — découvert de{" "}
                    <Text as="span" fontWeight="700" color="red.400">
                        {Math.abs(Number(row.coverage) || 0).toLocaleString("fr-FR")} {row.currency}
                    </Text>
                </Text>
            ))}
        </Box>
    );
}

/**
 * Renders a single incident notification with its context and jump link.
 * @param {Object} props - the normalized notification and the markRead action
 * @returns {React.ReactElement}
 */
function NotificationItem({ notification, onMarkRead }) {
    const navigate = useNavigate();
    const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const unreadBg = useColorModeValue("red.50", "whiteAlpha.100");

    const meta = INCIDENT_META[notification.type] || DEFAULT_INCIDENT_META;
    const stamp = notification.createdAt
        ? new Date(notification.createdAt).toLocaleString("fr-FR")
        : null;

    return (
        <Box
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="12px"
            p="16px"
            mb="12px"
            bg={notification.read ? "transparent" : unreadBg}
        >
            <Flex align="center" gap="10px" mb="6px" wrap="wrap">
                <Badge colorScheme={meta.colorScheme} borderRadius="6px" px="8px">
                    {notification.read ? "Lu" : "Nouveau"}
                </Badge>
                <Text fontSize="sm" fontWeight="700" color={titleColor}>{meta.title}</Text>
                {stamp && <Text fontSize="xs" color={hintColor}>· {stamp}</Text>}
            </Flex>
            <Text fontSize="sm" color={hintColor}>{meta.description}</Text>
            {notification.type === "treasury_under_covered" && (
                <TreasuryDetail payload={notification.payload} />
            )}
            <Flex gap="10px" mt="12px" wrap="wrap">
                {meta.link && (
                    <Button size="xs" variant="outline" onClick={() => navigate(meta.link)}>
                        {meta.linkLabel}
                    </Button>
                )}
                {!notification.read && (
                    <Button size="xs" variant="ghost" onClick={() => onMarkRead(notification.id)}>
                        Marquer comme lu
                    </Button>
                )}
            </Flex>
        </Box>
    );
}

export default NotificationItem;
